import { AssetReference, Notification } from "../../models/api/notification/notification";
import { NOTIFICATION_TYPES } from "../../enums/notificationTypes.enum";

export interface NotificationPresentation {
  notification: Notification;
  titleKey: string;
  textKey: string;
  value: string | null;
  reference: { value: string; additionalValue: string | undefined } | null;
}

/**
 * Creates the table presentation for a parsed notification.
 * @param notification The notification to present.
 * @returns Translation keys, template value, and reference label for the notification.
 */
export function createNotificationPresentation(notification: Notification): NotificationPresentation {
  return createKnownNotificationPresentation(notification);
}

/**
 * Maps a notification to its type-specific presentation values.
 * @param notification The notification to present.
 * @returns The presentation derived from the notification type and data.
 */
// eslint-disable-next-line complexity
function createKnownNotificationPresentation(notification: Notification): NotificationPresentation {
  switch (notification.type) {
    case NOTIFICATION_TYPES.WARNING:
    case NOTIFICATION_TYPES.CRITICAL:
      return createPresentation(notification, notification.type, notification.data.severity, {
        value: notification.data.label,
        additionalValue: createAssetReference(notification.data),
      });
    case NOTIFICATION_TYPES.ENERGY_LOSS:
    case NOTIFICATION_TYPES.REPAIR_REQUEST_CREATED:
    case NOTIFICATION_TYPES.INTERNAL_SERVICE_REMINDER:
    case NOTIFICATION_TYPES.EXTERNAL_SERVICE_REMINDER:
    case NOTIFICATION_TYPES.INTERNAL_SERVICE_CREATED:
    case NOTIFICATION_TYPES.EXTERNAL_SERVICE_CREATED:
    case NOTIFICATION_TYPES.INTERNAL_SERVICE_UPDATED:
    case NOTIFICATION_TYPES.EXTERNAL_SERVICE_UPDATED:
    case NOTIFICATION_TYPES.INTERNAL_SERVICE:
    case NOTIFICATION_TYPES.EXTERNAL_SERVICE:
      return createPresentation(notification, notification.type, null, {
        value: notification.data.label,
        additionalValue: createAssetReference(notification.data),
      });
    case NOTIFICATION_TYPES.REPAIR_REQUEST_UPDATED:
      return createPresentation(notification, notification.type, notification.data.status, {
        value: notification.data.label,
        additionalValue: createAssetReference(notification.data),
      });
    case NOTIFICATION_TYPES.PREDICTIVE_MAINTENANCE_CREATED:
      return createPresentation(notification, notification.type, notification.data.type, {
        value: notification.data.label,
        additionalValue: undefined,
      });
    case NOTIFICATION_TYPES.PREDICTIVE_MAINTENANCE_UPDATED:
      return createPresentation(notification, notification.type, null, {
        value: notification.data.label,
        additionalValue: undefined,
      });
    default: {
      const unsupportedNotification = notification as { type: unknown };
      throw new Error(`Unsupported notification type: ${String(unsupportedNotification.type)}`);
    }
  }
}

/**
 * Builds translation keys and display fields for a notification template.
 * @param notification The source notification.
 * @param template The translation template name.
 * @param value The optional template value key.
 * @param reference The optional reference label.
 * @returns The completed notification presentation.
 */
function createPresentation(
  notification: Notification,
  template: string,
  value: string | null,
  reference: { value: string; additionalValue: string | undefined } | null
): NotificationPresentation {
  const translationBase = `notifications.notification_templates.${template}`;
  return {
    notification,
    titleKey: `${translationBase}.title`,
    textKey: `${translationBase}.text`,
    value,
    reference,
  };
}

function createAssetReference(data: {
  label: string;
  building?: AssetReference | null;
  location?: AssetReference | null;
  customer?: AssetReference | null;
}): string {
  return [data.label, data.building?.label ?? "-", data.location?.label ?? "-", data.customer?.label ?? "-"].join(
    " / "
  );
}
